/**
 * Gemeinsame Konstanten und Typen für das Subscription-Feature.
 */

import { format } from 'date-fns'
import { activeDateFnsLocale, isEnLocale } from '../../lib/locale'

export const DURATION_OPTIONS = [1, 3, 6, 12] as const
export type DurationMonths = (typeof DURATION_OPTIONS)[number]

/** Badge-Stil je Abo-Status. Jeder Key braucht einen `status.*`-i18n-Text (DE+EN). */
export const SUBSCRIPTION_STATUS_CSS = {
  active: 'badge is-success',
  expired: 'badge is-danger',
  cancel_at_period_end: 'badge is-warning',
  past_due: 'badge is-danger',
  canceled: 'badge is-danger',
} as const

/** Neutraler Fallback: unbekannte Status duerfen nie als "aktiv"/gruen erscheinen. */
export const SUBSCRIPTION_STATUS_CSS_FALLBACK = 'badge'

/**
 * Pill-Klasse je HISTORIEN-Status (kanonisches BE-Set), Prototyp-Klassen aus
 * assets/tokens.css (`.pill`/`.pill-*`, projektweit in utilities.css portiert).
 *
 * Bewusst getrennt von SUBSCRIPTION_STATUS_CSS: in der Historie ist "canceled"
 * kein Fehlerzustand (das Abo lief regulaer weiter bis zum Periodenende),
 * sondern ein neutral-warnender Hinweis — "expired" dagegen ist der harte
 * Ablauf. Farbvorgabe: active gruen, canceled orange, expired rot,
 * trialing blau, alles Uebrige (inkl. "Ended") neutrales `.pill` wie im
 * Prototyp (subscription.html, Starter-Zeile).
 */
export const HISTORY_STATUS_PILL: Record<string, string> = {
  active: 'pill pill-success',
  cancel_at_period_end: 'pill pill-warning',
  canceled: 'pill pill-warning',
  expired: 'pill pill-danger',
  trialing: 'pill pill-info',
}

export const HISTORY_STATUS_PILL_FALLBACK = 'pill'

/** Pill-Klasse fuer einen Historien-Status; unbekannte Werte bleiben neutral. */
export function historyStatusBadge(status: string): { className: string } {
  return { className: HISTORY_STATUS_PILL[status] ?? HISTORY_STATUS_PILL_FALLBACK }
}

/**
 * Issue #143: Effektiver Anzeige-Status eines Historien-Eintrags.
 *
 * Das Backend liefert ein gekuendigtes, aber noch laufendes Abo weiterhin mit
 * status:'active' — nur `cancel_at_period_end` bzw. ein gesetztes `expires_at`
 * in der Zukunft signalisieren das bevorstehende Ende (dieselbe Herleitung
 * wie die Hero-Karte in ActiveSubscription.tsx ueber
 * `subscription.cancel_at_period_end`). Ohne diesen Schritt zeigt die
 * Historie "Active" weiter an, waehrend die Statuskarte oben bereits
 * "Expiring" meldet.
 */
export function historyDisplayStatus(item: {
  status: string
  cancel_at_period_end: boolean
  expires_at: string | null
}): string {
  if (item.status !== 'active') return item.status
  const expiresInFuture =
    item.expires_at != null && new Date(item.expires_at).getTime() > Date.now()
  return item.cancel_at_period_end || expiresInFuture ? 'cancel_at_period_end' : item.status
}

/**
 * Issue #141: Tab-Zuordnung der Historie ueber den effektiven Anzeige-Status
 * (siehe `historyDisplayStatus`) — "Aktiv" fasst laufende und auslaufende
 * Abos zusammen, "Beendet" alles andere (expired/canceled/past_due/…).
 */
export function historyTabBucket(displayStatus: string): 'active' | 'ended' {
  return displayStatus === 'active' || displayStatus === 'cancel_at_period_end' ? 'active' : 'ended'
}

/**
 * Stripe-Abo-ID gekuerzt anzeigen (sub_1P...x4Kz).
 * v1 zeigte die volle ID; die Kuerzung ist eine bewusste Produktentscheidung
 * (Support-Screenshots/Screensharing) — die volle ID bleibt per Copy-Button
 * und `title` erreichbar.
 */
export function truncateStripeId(id: string): string {
  if (id.length <= 14) return id
  return `${id.slice(0, 6)}...${id.slice(-4)}`
}

/**
 * Erkennt rohe Stripe-/Processor-IDs (z. B. "price_1Tg9I8I7HDLGcmJJWHfoN73Y"),
 * die bei unbekanntem processor_plan als plan_label durchgereicht werden
 * (SubscriptionHistorySerializer#plan_info_for-Fallback). Solche Strings
 * duerfen nie als "Plan-Name" angezeigt werden — das Prototyp-Design kennt
 * nur lesbare Plan-Namen in der Historie-Spalte.
 */
export function looksLikeProcessorId(value: string): boolean {
  return /^(price|prod|sub|plan|cus)_[A-Za-z0-9]+$/.test(value)
}

/**
 * Zentrale Ablauf-Erkennung (Finding 8 / F8-Nachfix).
 *
 * Das Backend liefert das Abo-Objekt auch nach Ablauf weiter (nur `status`
 * wechselt auf 'expired'). Jede UI, die "aktives Abo" kommuniziert, MUSS
 * darum ueber diesen Helper gehen — sonst wirkt ein abgelaufenes Abo aktiv.
 */
export function isExpiredSubscription(
  subscription: { status?: string } | null | undefined,
): boolean {
  return subscription?.status === 'expired'
}

/** Laufzeit-/Ablaufdatum lokalisiert formatieren; Fallback auf den Rohwert. */
export function formatSubscriptionDate(dateStr: string): string {
  try {
    return format(new Date(dateStr), isEnLocale() ? 'MMMM d, yyyy' : 'dd. MMMM yyyy', {
      locale: activeDateFnsLocale(),
    })
  } catch {
    return dateStr
  }
}
